import { FC, useState } from 'react';

import { 
  Box,
  TextField,
  Typography, 
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Button } from '@gisce/oficina-virtual-components';
import API from "@/services/api";
import { ContractDisplayTypes } from '../Contracts/types/contracts';
import { useContracts } from '../Contracts/hooks/useContracts';
import useAuth from '../Authentication/hooks/useAuth';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

enum STATUS {
  INIT,
  EDITING,
  SENDING
};

type TextFieldEditProps = {
  initialValue?: string,
  label: string,
  onUpdated: (value: string) => Promise<boolean>;
}

const TextFieldEdit: FC<TextFieldEditProps> = ({
  initialValue='', 
  label,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(STATUS.INIT);
  const [value, setValue] = useState(initialValue ?? "");
  const [error, setError] = useState<string | null>(null);

  const onClickEdit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (status === STATUS.INIT) {
      setStatus(STATUS.EDITING);
    } else if (status === STATUS.EDITING) {
      setStatus(STATUS.SENDING);
      if (onUpdated) {
        const updated = await onUpdated(value);
        if (updated) {
          setStatus(STATUS.INIT);
          setError(null);
        } else {
          setStatus(STATUS.EDITING);
          setError(t("common:text.contract_modify_notification_fail_message"));
        }
      }
    }
  };

  const cancelEdit = () => {
    setValue('');
    setStatus(STATUS.INIT);
  }

  return (
    <Box display="flex" flexDirection="column" mt={2}>

      {/* Title label */}
      <Typography variant="caption" color="gray">{label}</Typography>

      {<Box display="flex" alignItems="center" gap={1}>
        {status == STATUS.INIT && <div>{value}</div>}

        {/* Fillable */}
        {(status == STATUS.SENDING || status == STATUS.EDITING) && <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)} 
          disabled={status == STATUS.SENDING}
          variant='outlined'
          size="small"
        />}

        {/* Fill or send button */}
        {(status == STATUS.SENDING || status == STATUS.EDITING || !value && status == STATUS.INIT ) && 
          <Button
            color={'primary'}
            variant={'text'}
            loading={status == STATUS.SENDING}
            onClick={onClickEdit}
          >
            {status == STATUS.EDITING ? 
              t("common:text.save") : t("common:text.complete")}
          </Button>
        }

        {/* Cancel button */}
        {status === STATUS.EDITING && <Button
            variant={'text'}
            sx={{ minWidth: '10px' }}
            onClick={cancelEdit}
          >
            {<Close color="primary"/>}
          </Button>
        }
      </Box>}

      {/*  Error */}
      {error && <Typography color="red" variant="caption">{error}</Typography>}
    </Box>
  );
}

export default TextFieldEdit;
